import React, { useState } from 'react';
import { FileText, Users, Search, Check, X } from 'lucide-react';
import { EntryPointCard, SelectDropdown, TagChip } from '../components';
import { useTriggerConfigState } from '../hooks';

/**
 * Trigger Configuration Content
 * Handles both form-based and manual entry triggers.
 * 
 * @param {Object} props
 * @param {Array} props.leads - All leads from Firestore
 * @param {Object} props.initialConfig - Initial trigger config from saved automation
 * @param {Function} props.onConfigChange - Callback when config changes
 * @param {Array} props.forms - Available forms for form-based trigger
 */
const TriggerConfigContent = ({
    leads = [],
    initialConfig = {},
    onConfigChange,
    forms = [],
}) => {
    const {
        entryType,
        setEntryType,
        selectedForm,
        setSelectedForm,
        selectedStatus,
        setSelectedStatus,
        selectedTags,
        addTag,
        removeTag,
        availableTags,
        searchQuery,
        setSearchQuery,
        searchedLeads,
        showLeadsPreview,
        setShowLeadsPreview,
        selectedCount,
        filteredCount,
        getFilteredLeads,
        availableStatuses,
        selectedLeadIds,
        toggleLeadSelection,
        isLeadSelected,
        selectAllFilteredLeads,
        deselectAllFilteredLeads,
    } = useTriggerConfigState({ initialConfig, leads, onConfigChange });

    const [tagInput, setTagInput] = useState('');

    const filteredLeads = getFilteredLeads();

    // Check if all filtered leads are selected
    const allFilteredSelected = filteredLeads.length > 0 &&
        filteredLeads.every(lead => isLeadSelected(lead.id));

    return (
        <div className="space-y-5">
            {/* Section Label */}
            <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-0.5">
                    How should leads enter this workflow?
                </label>
                <span className="text-[10px] text-stone-400">Entry Point</span>
            </div>

            {/* Entry Point Cards */}
            <div className="space-y-3">
                <EntryPointCard
                    selected={entryType === 'form'}
                    onClick={() => setEntryType('form')}
                    icon={FileText}
                    title="Connect to Form"
                    description="Automatically start workflow when a lead submits a specific form."
                />

                <EntryPointCard
                    selected={entryType === 'manual'}
                    onClick={() => setEntryType('manual')}
                    icon={Users}
                    title="Add Manually"
                    description="Select leads using filters or add them individually to this workflow."
                />
            </div>

            {/* Conditional Content based on Entry Type */}
            {entryType === 'form' && (
                <div className="space-y-4 pt-1">
                    {/* Separator dots */}
                    <div className="flex justify-center py-1">
                        <span className="text-stone-300 tracking-[0.3em] text-xs">···</span>
                    </div>

                    {/* Select Form */}
                    <div>
                        <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
                            Select Form
                        </label>
                        <SelectDropdown
                            value={selectedForm}
                            onChange={(e) => setSelectedForm(e.target.value)}
                            options={forms.map(f => ({ id: f.id, name: f.name }))}
                            placeholder="Choose a form..."
                        />
                    </div>

                    {/* Helper Text */}
                    <div className="flex items-start gap-2.5 py-1">
                        <div className="w-4 h-4 rounded-full border border-stone-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-[8px] text-stone-400 font-medium">i</span>
                        </div>
                        <span className="text-xs text-stone-500">Leads will enter automatically when form is submitted.</span>
                    </div>
                </div>
            )}

            {entryType === 'manual' && (
                <div className="space-y-4 pt-1">
                    {/* Lead Filters Header */}
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
                            Lead Filters
                        </label>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowLeadsPreview(!showLeadsPreview)}
                                className="text-xs text-stone-500 hover:text-stone-700 font-medium underline-offset-2 hover:underline"
                            >
                                {showLeadsPreview ? 'Hide' : 'View'} Matching ({filteredCount})
                            </button>
                            <span className="px-2.5 py-1 bg-stone-800 text-white text-[10px] font-semibold rounded">
                                {selectedCount} Selected
                            </span>
                        </div>
                    </div>

                    {/* Matching Leads Preview (toggleable) */}
                    {showLeadsPreview && (
                        <div className="space-y-2">
                            {/* Select All Checkbox */}
                            {filteredLeads.length > 0 && (
                                <div className="flex items-center gap-2 px-1">
                                    <button
                                        onClick={() => allFilteredSelected ? deselectAllFilteredLeads() : selectAllFilteredLeads()}
                                        className="flex items-center gap-2 text-xs text-stone-600 hover:text-stone-800 font-medium"
                                    >
                                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${allFilteredSelected
                                            ? 'bg-stone-800 border-stone-800'
                                            : 'border-stone-300'
                                            }`}>
                                            {allFilteredSelected && (
                                                <Check className="w-3 h-3 text-white" />
                                            )}
                                        </div>
                                        <span>
                                            {allFilteredSelected ? 'Deselect' : 'Select'} all {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
                                        </span>
                                    </button>
                                </div>
                            )}

                            {/* Leads List with Checkboxes */}
                            <div className="bg-white border border-stone-200 rounded-lg divide-y divide-stone-100 overflow-hidden max-h-48 overflow-y-auto">
                                {filteredLeads.length > 0 ? (
                                    filteredLeads.slice(0, 10).map((lead) => {
                                        const selected = isLeadSelected(lead.id);
                                        return (
                                            <button
                                                key={lead.id}
                                                onClick={() => {
                                                    toggleLeadSelection(lead.id);
                                                }}
                                                className={`w-full px-3 py-2.5 flex items-center gap-3 hover:bg-stone-50 transition cursor-pointer ${selected ? 'bg-blue-50' : ''
                                                    }`}
                                            >
                                                {/* Checkbox */}
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected
                                                    ? 'bg-stone-800 border-stone-800'
                                                    : 'border-stone-300'
                                                    }`}>
                                                    {selected && (
                                                        <Check className="w-3 h-3 text-white" />
                                                    )}
                                                </div>

                                                {/* Lead Info */}
                                                <div className="flex-1 min-w-0 text-left">
                                                    <span className="text-sm text-stone-700 font-medium block truncate">
                                                        {lead.fullName || lead.name || 'Unnamed Lead'}
                                                    </span>
                                                    <span className="text-xs text-stone-400 truncate block">
                                                        {lead.email || lead.phone || ''}
                                                    </span>
                                                </div>

                                                {/* Status Badge */}
                                                {lead.status && (
                                                    <span className="text-xs text-stone-400 ml-2">{lead.status}</span>
                                                )}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="px-3 py-4 text-center text-xs text-stone-400">
                                        No leads match current filters
                                    </div>
                                )}
                                {filteredLeads.length > 10 && (
                                    <div className="px-3 py-2 text-center text-xs text-stone-400 bg-stone-50">
                                        +{filteredLeads.length - 10} more leads
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Filter Fields */}
                    <div className="space-y-3">
                        {/* Status */}
                        <div>
                            <label className="block text-xs text-stone-500 mb-1.5">Status</label>
                            <SelectDropdown
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                options={availableStatuses.map(s => ({ id: s, name: s }))}
                                placeholder="All Statuses"
                            />
                        </div>

                        {/* Tags Filter */}
                        <div>
                            <label className="block text-xs text-stone-500 mb-1.5">Tags</label>
                            {/* Selected tags */}
                            {selectedTags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {selectedTags.map(tag => (
                                        <TagChip key={tag} label={tag} onRemove={() => removeTag(tag)} />
                                    ))}
                                </div>
                            )}
                            {/* Tag selector */}
                            {availableTags.length > 0 ? (
                                <select
                                    value=""
                                    onChange={(e) => {
                                        if (e.target.value) addTag(e.target.value);
                                    }}
                                    className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-700 outline-none focus:border-stone-400 appearance-none cursor-pointer"
                                >
                                    <option value="">Add a tag filter...</option>
                                    {availableTags
                                        .filter(t => !selectedTags.includes(t))
                                        .map(tag => (
                                            <option key={tag} value={tag}>{tag}</option>
                                        ))}
                                </select>
                            ) : (
                                <p className="text-xs text-stone-400 italic">No tags found on leads</p>
                            )}
                        </div>
                    </div>

                    {/* OR Add Individual Lead */}
                    <div>
                        <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
                            Or Add Individual Lead
                        </label>
                        <div className="relative">
                            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by name, email, or phone..."
                                className="w-full bg-white border border-stone-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-stone-700 outline-none focus:border-stone-400 placeholder-stone-400"
                            />
                        </div>

                        {/* Search Results */}
                        {searchedLeads.length > 0 && (
                            <div className="mt-2 bg-white border border-stone-200 rounded-lg divide-y divide-stone-100 overflow-hidden max-h-40 overflow-y-auto">
                                {searchedLeads.map((lead) => (
                                    <button
                                        key={lead.id}
                                        onClick={() => toggleLeadSelection(lead.id)}
                                        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-stone-50 transition"
                                    >
                                        <div className="flex-1 min-w-0 text-left">
                                            <span className="text-sm text-stone-700 font-medium block truncate">
                                                {lead.fullName || lead.name || 'Unnamed Lead'}
                                            </span>
                                            <span className="text-xs text-stone-400 truncate block">
                                                {lead.email || lead.phone || ''}
                                            </span>
                                        </div>
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ml-2 ${isLeadSelected(lead.id)
                                            ? 'bg-stone-800 border-stone-800'
                                            : 'border-stone-300'
                                            }`}>
                                            {isLeadSelected(lead.id) && (
                                                <Check className="w-3 h-3 text-white" />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Selected Individual Leads */}
                        {selectedLeadIds.length > 0 && (
                            <div className="mt-3">
                                <label className="block text-[10px] text-stone-400 mb-1.5">
                                    Selected ({selectedLeadIds.length})
                                </label>
                                <div className="flex flex-wrap gap-1.5">
                                    {selectedLeadIds.map(id => {
                                        const lead = leads.find(l => l.id === id);
                                        if (!lead) return null;
                                        return (
                                            <span
                                                key={id}
                                                className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded flex items-center gap-1"
                                            >
                                                {lead.fullName || lead.name || lead.email || id}
                                                <X
                                                    className="w-3 h-3 cursor-pointer hover:text-stone-900"
                                                    onClick={() => toggleLeadSelection(id)}
                                                />
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Info Box */}
                    {selectedCount > 0 ? (
                        <div className="flex items-start gap-2.5 p-3.5 bg-green-50 border border-green-200/80 rounded-xl">
                            <div className="w-4 h-4 rounded-full border border-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-2.5 h-2.5 text-green-600" />
                            </div>
                            <p className="text-xs text-green-700 leading-relaxed">
                                {selectedCount} lead{selectedCount !== 1 ? 's' : ''} selected for this workflow.
                            </p>
                        </div>
                    ) : (
                        <div className="flex items-start gap-2.5 p-3.5 bg-orange-50 border border-orange-200/80 rounded-xl">
                            <div className="w-4 h-4 rounded-full border border-orange-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-[8px] text-orange-500 font-bold">!</span>
                            </div>
                            <p className="text-xs text-orange-700 leading-relaxed">
                                No leads selected. Use filters to find leads, then select them individually.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TriggerConfigContent;
